import EventRoundedIcon from "@mui/icons-material/EventRounded";
import { Box, InputAdornment } from "@mui/material";
import { DateTime } from "luxon";
import type { CSSProperties, FC, ReactElement } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { ICON_SIZE, INK } from "@/theme/tokens";
import { SystemDatePickerPopup } from "../Popups/SystemDatePickerPopup";
import { SystemValidationTextField } from "./SystemValidationTextField";

/** The keys that open the calendar from the focused field, as a select's trigger answers them. */
const OPEN_KEYS = new Set(["Enter", " ", "ArrowDown"]);

type Props = {
	value: DateTime;
	/** A day picked on the calendar (null if the calendar cleared it). */
	onChange: (date: DateTime | null) => void;
	/** The field's accessible name; its visible label is the caller's (a settings line's title, a radio option). */
	ariaLabel: string;
	/** The open calendar's anchor, or null while it's shut: the caller owns it, so it can shut the calendar itself. */
	anchorEl: HTMLElement | null;
	onAnchorElChange: (anchorEl: HTMLElement | null) => void;
	disabled: boolean;
	/**
	 * Why it's disabled, as the tooltip on the box around it (a disabled input takes no hover).
	 * DEFAULT: absent, a disabled field shows no tooltip: a field disabled with the form around it needs no reason of its own.
	 */
	disabledReason?: string;
	/**
	 * The earliest and latest days the calendar offers.
	 * DEFAULT: absent, no bound on that side: most dates (a report's day) may be any day.
	 */
	minDate?: DateTime;
	maxDate?: DateTime;
	width: CSSProperties["width"];
	dataTestId: string;
};

/**
 * A read-only field that shows a date in the reader's own locale ("Jun 3, 2030") with a calendar icon, and opens the
 * calendar (`SystemDatePickerPopup`) on a click or Enter / Space / ArrowDown. For a single date picked from a calendar
 * rather than typed. The calendar portals to `<body>`, so a field inside a grid row or a popover isn't clipped by it,
 * and it is stamped as a portal overlay, so picking a day doesn't read as a click outside an enclosing popover.
 * A click on the field stops at it: it opens the calendar, not the handlers of a row around it.
 */
export const SystemDatePickerField: FC<Props> = ({
	value,
	onChange,
	ariaLabel,
	anchorEl,
	onAnchorElChange,
	disabled,
	disabledReason,
	minDate,
	maxDate,
	width,
	dataTestId,
}): ReactElement => {
	const open = anchorEl !== null;
	const toggle = (field: HTMLElement): void => {
		if (disabled) return;
		onAnchorElChange(open ? null : field);
	};

	return (
		<>
			<InHouseTooltip title={disabled && disabledReason !== undefined ? disabledReason : ""}>
				<Box sx={{ width }}>
					<SystemValidationTextField
						value={value.toLocaleString(DateTime.DATE_MED)}
						disabled={disabled}
						fullWidth
						reserveHeightForHelperText={false}
						inputProps={{
							readOnly: true,
							inputProps: { "aria-label": ariaLabel, "aria-haspopup": "dialog", "aria-expanded": open },
							endAdornment: (
								<InputAdornment position="end">
									<EventRoundedIcon
										aria-hidden
										sx={(theme) => ({
											fontSize: ICON_SIZE.CONTROL,
											color: disabled ? theme.palette.action.disabled : open ? theme.palette.primary.main : INK.MUTED,
										})}
									/>
								</InputAdornment>
							),
						}}
						injectedProps={{
							onClick: (event) => {
								event.stopPropagation();
								toggle(event.currentTarget);
							},
						}}
						onKeyDown={(event) => {
							if (!OPEN_KEYS.has(event.key)) return;
							event.preventDefault();
							toggle(event.currentTarget);
						}}
						inputSx={{ cursor: disabled ? "default" : "pointer", "& input": { cursor: disabled ? "default" : "pointer" } }}
						dataTestId={dataTestId}
					/>
				</Box>
			</InHouseTooltip>
			{anchorEl && (
				<SystemDatePickerPopup
					anchorEl={anchorEl}
					open={open}
					onClose={() => onAnchorElChange(null)}
					onChange={onChange}
					value={value}
					overridePickerProps={{ minDate, maxDate }}
				/>
			)}
		</>
	);
};
